/**
 * Payment Collection Page - Track payment plans, collection progress, and upcoming payments
 *
 * Features:
 * - Payment plan tracking (structured and custom plans)
 * - Progress visualization with progress bars
 * - Status indicators (Overdue, Due Soon, Current, Paid)
 * - Manual completion checkbox for custom plans
 * - Filtering by closer, status, plan type
 * - Search and pagination
 * - Uses global date range and filter contexts (NUQS)
 */

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { PageHeader } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useOfferDashboard } from "../context/OfferDashboardContext";

// Icons
const ClockIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const InfoIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatDate = (date: Date | string | null) => {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(d);
};

// Types
interface PaymentCustomer {
  prospectEmail: string;
  prospectName: string;
  closerName: string;
  paymentPlan: string;
  isCustomPlan: boolean;
  totalPayments: number | null;
  paymentsMade: number;
  amountCollected: number;
  amountRemaining: number | null;
  totalAmount: number | null;
  initialSaleDate: string | null;
  lastPaymentDate: string | null;
  nextPaymentDue: string | null;
  daysUntilDue: number | null;
  status: "paid_in_full" | "current" | "due_soon" | "overdue";
  isFullyPaid: boolean;
}

interface FilterOptions {
  closers: string[];
  setters: string[];
  paymentPlans: string[];
}

interface Summary {
  total: number;
  overdue: number;
  dueSoon: number;
  current: number;
  paidInFull: number;
}

// Status badge component
function StatusBadge({ status }: { status: PaymentCustomer["status"] }) {
  const styles: Record<typeof status, string> = {
    overdue: "bg-red-500 text-white",
    due_soon: "bg-amber-500 text-white",
    current: "bg-blue-500 text-white",
    paid_in_full: "bg-green-500 text-white",
  };
  const labels: Record<typeof status, string> = {
    overdue: "Overdue",
    due_soon: "Due Soon",
    current: "Current",
    paid_in_full: "Paid",
  };

  return (
    <span className={`px-2.5 py-1 rounded text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

// Checkbox component
function Checkbox({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
        checked
          ? "bg-[var(--brand-primary)] border-[var(--brand-primary)]"
          : "border-muted-foreground/40 hover:border-[var(--brand-primary)]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      {checked && (
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
}

// Pagination component
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const pages: (number | "...")[] = [];

  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-center gap-2 mt-4">
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border border-border bg-card hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Previous
      </button>

      {pages.map((page, i) =>
        page === "..." ? (
          <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground">
            ...
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
              currentPage === page
                ? "bg-[var(--brand-primary)] text-white"
                : "border border-border bg-card hover:bg-muted"
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border border-border bg-card hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

export function PaymentCollection() {
  // Use global contexts for date range and filters (synced via NUQS)
  const { dateRange } = useDateRange();
  const { closers: globalClosers, setters: globalSetters } = useFilters();
  const offerDashboard = useOfferDashboard();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState<PaymentCustomer[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ closers: [], setters: [], paymentPlans: [] });
  const [summary, setSummary] = useState<Summary>({ total: 0, overdue: 0, dueSoon: 0, current: 0, paidInFull: 0 });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, totalItems: 0, totalPages: 0 });

  // Page-specific filters (not shared with other pages)
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [hidePaid, setHidePaid] = useState(false);

  // UI state
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [togglingEmail, setTogglingEmail] = useState<string | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      // Use global date range from header
      if (dateRange.startDate) params.append("startDate", dateRange.startDate.toISOString().split("T")[0] ?? "");
      if (dateRange.endDate) params.append("endDate", dateRange.endDate.toISOString().split("T")[0] ?? "");

      // Use global filters from context (NUQS)
      if (globalClosers.length > 0) params.append("closer", globalClosers.join(","));
      if (globalSetters.length > 0) params.append("setter", globalSetters.join(","));

      // Page-specific filters
      if (searchQuery) params.append("search", searchQuery);
      if (statusFilter) params.append("status", statusFilter);
      if (planFilter) params.append("paymentPlan", planFilter);
      if (hidePaid) params.append("hidePaid", "true");
      if (offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug) {
        params.append("dashboard", offerDashboard.dashboardSlug);
      }

      params.append("page", pagination.page.toString());
      params.append("pageSize", pagination.pageSize.toString());

      const response = await fetch(`/api/payments/collection?${params}`);
      const result = await response.json();

      if (result.success) {
        setCustomers(result.data.customers);
        setFilterOptions(result.data.filterOptions);
        setSummary(result.data.summary);
        setPagination(result.data.pagination);
      }
    } catch (error) {
      console.error("Failed to fetch payment collection data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, globalClosers, globalSetters, searchQuery, statusFilter, planFilter, hidePaid, pagination.page, pagination.pageSize, offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle complete handler
  const handleToggleComplete = async (email: string, currentStatus: boolean) => {
    setTogglingEmail(email);
    try {
      const params = new URLSearchParams();
      if (offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug) {
        params.set("dashboard", offerDashboard.dashboardSlug);
      }

      const response = await fetch(`/api/payments/toggle-complete${params.toString() ? `?${params.toString()}` : ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prospectEmail: email, isComplete: !currentStatus }),
      });

      if (response.ok) {
        // Optimistic update
        setCustomers((prev) =>
          prev.map((c) =>
            c.prospectEmail.toLowerCase() === email.toLowerCase()
              ? { ...c, isFullyPaid: !currentStatus, status: !currentStatus ? "paid_in_full" : "current" }
              : c
          )
        );
        // Refetch for accurate data
        await fetchData();
      }
    } catch (error) {
      console.error("Failed to toggle complete:", error);
    } finally {
      setTogglingEmail(null);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchQuery, statusFilter, planFilter, hidePaid, globalClosers, globalSetters, dateRange]);

  // Loading skeleton
  if (isLoading && customers.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Payment Collection" subtitle="Track payment plans, collection progress, and upcoming payments" />
        <div className="bg-card rounded-xl border border-border">
          <div className="p-4 border-b border-border">
            <div className="h-6 bg-muted rounded w-48" />
          </div>
          <div className="p-4 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Payment Collection" subtitle="Track payment plans, collection progress, and upcoming payments" />

      {/* Payment Collection Tracker Section */}
      <div className="bg-card rounded-xl border border-border">
        {/* Header with How It Works */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">
              <ClockIcon />
            </div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Payment Collection Tracker</h2>
              <button
                type="button"
                onClick={() => setShowHowItWorks(!showHowItWorks)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <InfoIcon />
                <span>How it works</span>
                {showHowItWorks ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </button>
            </div>
          </div>

          {/* Expandable How It Works Section */}
          <motion.div
            initial={false}
            animate={{ height: showHowItWorks ? "auto" : 0, opacity: showHowItWorks ? 1 : 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 p-4 bg-muted/50 rounded-lg text-sm space-y-3">
              <h3 className="font-semibold">How Payment Collection Works</h3>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  <span className="font-medium text-foreground">Tracking customers:</span> Any customer with a payment plan
                  (e.g., "$1K per month - for 4 months") will appear here, regardless of their initial call outcome (Closed,
                  Deposit Collected, Follow-up Scheduled, etc.).
                </p>
                <p>
                  <span className="font-medium text-foreground">Recording a payment:</span> When a customer makes a payment,
                  add a new call entry in the EOC sheet with <span className="font-semibold">Call Outcome = "MRR"</span> and
                  include the cash collected. This will automatically increment their payment count.
                </p>
                <p>
                  <span className="font-medium text-foreground">Progress tracking:</span> For plans like "$1K/month for 4
                  months", each MRR entry counts as one payment. When 4 MRR entries are recorded, the customer is marked as
                  "Paid in Full".
                </p>
                <p>
                  <span className="font-medium text-foreground">Custom plans:</span> For customers with "Custom Plan", use
                  the checkbox in the table to manually mark them as complete when all payments are received.
                </p>
                <p>
                  <span className="font-medium text-foreground">Next payment due:</span> Calculated as 30 days after the
                  last MRR entry (or initial sale if no payments yet).
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Table Filters Row */}
        <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[250px] max-w-md">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search by prospect or closer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]"
            />
          </div>

          {/* Status Filter */}
          <select
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[120px]"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="overdue">Overdue</option>
            <option value="due_soon">Due Soon</option>
            <option value="current">Current</option>
            <option value="paid_in_full">Paid in Full</option>
          </select>

          {/* Plan Filter */}
          <select
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[120px]"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">All Plans</option>
            {filterOptions.paymentPlans.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          {/* Hide Paid Toggle */}
          <button
            type="button"
            onClick={() => setHidePaid(!hidePaid)}
            className={`h-9 px-4 rounded-md text-sm font-medium transition-colors ${
              hidePaid
                ? "bg-[var(--brand-primary)] text-white"
                : "border border-input bg-background hover:bg-muted"
            }`}
          >
            Hide Paid
          </button>
        </div>

        {/* Summary Row */}
        <div className="px-4 py-2 text-sm text-muted-foreground border-b border-border">
          Showing {customers.length} of {summary.total} customers
          {summary.overdue > 0 && (
            <span className="ml-2 text-red-500">({summary.overdue} overdue)</span>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 font-medium text-muted-foreground text-sm">Customer</th>
                <th className="text-left p-4 font-medium text-muted-foreground text-sm">Closer</th>
                <th className="text-left p-4 font-medium text-muted-foreground text-sm">Plan</th>
                <th className="text-left p-4 font-medium text-muted-foreground text-sm">Progress</th>
                <th className="text-right p-4 font-medium text-muted-foreground text-sm">Collected</th>
                <th className="text-right p-4 font-medium text-muted-foreground text-sm">Remaining</th>
                <th className="text-left p-4 font-medium text-muted-foreground text-sm">Next Due</th>
                <th className="text-center p-4 font-medium text-muted-foreground text-sm">Status</th>
                <th className="text-center p-4 font-medium text-muted-foreground text-sm">Complete</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-muted-foreground">
                    No customers with payment plans found.
                  </td>
                </tr>
              ) : (
                customers.map((customer, index) => {
                  const progressPercent = customer.totalPayments
                    ? Math.min(100, (customer.paymentsMade / customer.totalPayments) * 100)
                    : 0;

                  return (
                    <motion.tr
                      key={customer.prospectEmail}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`border-b border-border ${
                        index % 2 === 0 ? "bg-background" : "bg-muted/20"
                      } hover:bg-muted/40 transition-colors`}
                    >
                      {/* Customer */}
                      <td className="p-4">
                        <div className="flex items-start gap-2">
                          <UserIcon />
                          <div>
                            <div className="font-medium">{customer.prospectName || "Unknown"}</div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MailIcon />
                              <span>{customer.prospectEmail}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Closer */}
                      <td className="p-4 text-sm">{customer.closerName || "—"}</td>

                      {/* Plan */}
                      <td className="p-4 text-sm">{customer.paymentPlan}</td>

                      {/* Progress */}
                      <td className="p-4">
                        {customer.isCustomPlan ? (
                          <span className="text-sm text-muted-foreground">
                            {customer.paymentsMade} payments
                          </span>
                        ) : customer.totalPayments ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span>
                                {customer.paymentsMade} / {customer.totalPayments}
                              </span>
                              <span className="text-muted-foreground">{Math.round(progressPercent)}%</span>
                            </div>
                            <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[var(--brand-primary)] rounded-full transition-all"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </td>

                      {/* Collected */}
                      <td className="p-4 text-right">
                        <span className="text-green-600 font-medium">
                          $ {formatCurrency(customer.amountCollected).replace("$", "")}
                        </span>
                      </td>

                      {/* Remaining */}
                      <td className="p-4 text-right text-sm">
                        {customer.amountRemaining !== null ? (
                          <span className="text-[var(--brand-primary)] font-medium">
                            $ {formatCurrency(customer.amountRemaining).replace("$", "")}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>

                      {/* Next Due */}
                      <td className="p-4">
                        {customer.isFullyPaid ? (
                          <span className="text-muted-foreground">—</span>
                        ) : customer.nextPaymentDue ? (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1 text-sm">
                              <CalendarIcon />
                              <span>{formatDate(customer.nextPaymentDue)}</span>
                            </div>
                            {customer.daysUntilDue !== null && (
                              <div
                                className={`flex items-center gap-1 text-xs ${
                                  customer.daysUntilDue < 0
                                    ? "text-red-500"
                                    : customer.daysUntilDue <= 7
                                    ? "text-amber-500"
                                    : "text-muted-foreground"
                                }`}
                              >
                                <ClockIcon />
                                <span>
                                  {customer.daysUntilDue === 0
                                    ? "Today"
                                    : customer.daysUntilDue > 0
                                    ? `${customer.daysUntilDue}d`
                                    : `${Math.abs(customer.daysUntilDue)}d overdue`}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="p-4 text-center">
                        <StatusBadge status={customer.status} />
                      </td>

                      {/* Complete */}
                      <td className="p-4 text-center">
                        {customer.isCustomPlan ? (
                          <div className="flex justify-center">
                            <Checkbox
                              checked={customer.isFullyPaid}
                              onChange={() => handleToggleComplete(customer.prospectEmail, customer.isFullyPaid)}
                              disabled={togglingEmail === customer.prospectEmail}
                            />
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-border">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
            />
          </div>
        )}
      </div>
    </div>
  );
}
